import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ShoppingBag, ArrowRight, Gift } from "lucide-react";
import { CartLine } from "./CartLine";
import { useApp } from "../context/AppContext";
import api, { fmt } from "../lib/api";
import { cartWeightG, receptionFee } from "../lib/shipping";
import { isPickOnly } from "../lib/limited";

const DR_MODES = ["pickup", "local", "delivery", "shipping", "chronopost"];

const readPickup = () => { try { return JSON.parse(localStorage.getItem("cs_pickup")) || {}; } catch { return {}; } };
const DAY_FMT = new Intl.DateTimeFormat("fr-FR", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });

function PickupPicker({ minPrep }) {
  const [dates, setDates] = useState([]);
  const [pick, setPick] = useState(readPickup);
  const [slots, setSlots] = useState([]);
  useEffect(() => {
    api.get("/availability/range", { params: { days: 62 } }).then((r) => {
      const earliest = new Date(Date.now() + minPrep * 3600000); earliest.setHours(0, 0, 0, 0);
      const lim = `${earliest.getFullYear()}-${String(earliest.getMonth() + 1).padStart(2, "0")}-${String(earliest.getDate()).padStart(2, "0")}`;
      setDates((r.data?.dates || []).filter((d) => !d.blocked && !d.day_full && d.date >= lim).map((d) => d.date));
    }).catch(() => {});
  }, [minPrep]);
  useEffect(() => {
    if (!pick.date) { setSlots([]); return; }
    api.get("/availability", { params: { date: pick.date } }).then((r) => setSlots(r.data?.blocked ? [] : (r.data?.slots || []))).catch(() => {});
  }, [pick.date]);
  const save = (p) => { setPick(p); localStorage.setItem("cs_pickup", JSON.stringify(p)); };
  const dateOk = dates.includes(pick.date);
  return (
    <div className="cs-dr-pk" data-testid="drawer-pickup">
      <label>Date de retrait
        <select data-testid="drawer-pickup-date" value={dateOk ? pick.date : ""} onChange={(e) => save({ date: e.target.value, slot: "" })}>
          <option value="">Choisir</option>
          {dates.map((d) => <option key={d} value={d}>{DAY_FMT.format(new Date(d))}</option>)}
        </select>
      </label>
      <label>Créneau
        <select data-testid="drawer-pickup-slot" disabled={!dateOk} value={dateOk ? pick.slot || "" : ""} onChange={(e) => save({ ...pick, slot: e.target.value })}>
          <option value="">Choisir</option>
          {slots.map((s) => <option key={s.slot} value={s.slot} disabled={s.full}>{s.full ? `${s.slot} (complet)` : s.slot}</option>)}
        </select>
      </label>
    </div>
  );
}

function FreeShipProgress({ settings, mode, subtotal, weightG }) {
  const cfg = settings.free_shipping_progress || {};
  const r = settings.reception?.[mode];
  if (!cfg.enabled || !r || !(receptionFee({ ...r, free_threshold: null }, mode, subtotal, weightG) > 0)) return null;
  if (r.free_max_kg != null && r.free_max_kg !== "" && weightG / 1000 > Number(r.free_max_kg)) return null;
  const t = Number(r.free_threshold);
  if (r.free_threshold == null || r.free_threshold === "" || !(t > 0)) return null;
  const left = Math.max(0, t - subtotal);
  const msg = left > 0
    ? (cfg.message || "Plus que {montant} pour la livraison offerte").replace("{montant}", fmt(left))
    : (cfg.success_message || "Livraison offerte !");
  return (
    <div className="cs-dr-fs" data-testid="drawer-free-shipping">
      <p>{msg}</p>
      <div className="cs-dr-fs-bar"><span style={{ width: `${Math.min(100, (subtotal / t) * 100)}%` }} /></div>
    </div>
  );
}

function ReceptionPicker({ subtotal, cart }) {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState(null);
  const [stored, setMode] = useState(() => localStorage.getItem("cs_reception_mode") || "pickup");
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
    api.get("/products").then((r) => { const m = {}; (r.data || []).forEach((p) => { m[p.id] = p; }); setProducts(m); }).catch(() => setProducts({}));
  }, []);
  if (!settings || !products) return null;
  const reception = settings.reception || {};
  const lines = cart.map((i) => products[i.product_id]).filter(Boolean);
  const maxPrep = lines.reduce((mx, p) => Math.max(mx, Number(p.prep_hours) || 0), 0);
  const allowed = (k) => lines.every((p) => (isPickOnly(p) ? k === "pickup" : !Array.isArray(p.reception_modes) || !p.reception_modes.length || p.reception_modes.includes(k)));
  const opts = DR_MODES.filter((k) => reception[k] && reception[k].active !== false && allowed(k));
  if (!opts.length) return null;
  const mode = opts.includes(stored) ? stored : opts[0];
  const pick = (k) => { setMode(k); localStorage.setItem("cs_reception_mode", k); };
  const weightG = cartWeightG(cart, settings.category_weights);
  const feeOf = (r, k) => receptionFee(r, k, subtotal, weightG);
  return (
    <div className="cs-dr-rec" data-testid="drawer-reception">
      <h4>Retrait ou livraison</h4>
      {opts.map((k) => {
        const fee = feeOf(reception[k], k);
        return (
          <button key={k} type="button" data-testid={`drawer-mode-${k}`} aria-pressed={mode === k} className={mode === k ? "on" : ""} onClick={() => pick(k)}>
            <span className="cs-dr-radio" />
            <span className="cs-dr-rec-l">{reception[k].label}</span>
            <b>{fee > 0 ? fmt(fee) : "Gratuit"}</b>
          </button>
        );
      })}
      {mode === "pickup" && opts.includes("pickup") && <PickupPicker minPrep={Math.max(Number(settings.min_prep_hours ?? 24), maxPrep)} />}
      <FreeShipProgress settings={settings} mode={mode} subtotal={subtotal} weightG={weightG} />
    </div>
  );
}

const BOXES = [[4, 12], [6, 18], [8, 22], [12, 32], [16, 40]];

function BoxIncentive({ cart }) {
  const cookieLines = cart.filter((l) => l.kind === "product");
  const units = cookieLines.reduce((s, l) => s + l.quantity, 0);
  const spent = cookieLines.reduce((s, l) => s + l.unit_price * l.quantity, 0);
  if (units <= 0) return null;
  const avg = spent / units;

  let title, sub;
  const target = BOXES.find(([size]) => size > units);
  const largestReached = [...BOXES].reverse().find(([size]) => size <= units);
  if (target) {
    const [size, price] = target;
    const need = size - units;
    const savings = Math.max(0, size * avg - price);
    title = `Plus que ${need} cookie${need > 1 ? "s" : ""} pour une box de ${size}`;
    sub = savings > 0.05 ? `Tu économiserais ${fmt(savings)} en passant en box.` : "Compose ta box au meilleur prix.";
  } else if (largestReached) {
    const [size, price] = largestReached;
    const savings = Math.max(0, size * avg - price);
    title = savings > 0.05 ? `Moins cher en box : économise ${fmt(savings)}` : "Passe en box pour le meilleur prix";
    sub = `La box de ${size} cookies revient à ${fmt(price)}.`;
  } else return null;

  return (
    <div data-testid="cart-box-incentive" className="rounded-2xl bg-[#FBEDE2] border border-[#F4A06C] p-4 flex items-start gap-3">
      <span className="w-9 h-9 shrink-0 grid place-items-center rounded-full bg-[#C6531A] text-white"><Gift size={18} /></span>
      <div className="min-w-0 flex-1">
        <p className="font-bold text-[#A8410F] text-sm leading-snug">{title}</p>
        <p className="text-xs text-[#6E5546] mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

export default function CartDrawer() {
  const { drawerOpen, closeCart, cart, updateQty, removeFromCart, cartSubtotal, cartCount } = useApp();
  const nav = useNavigate();

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") closeCart(); };
    if (drawerOpen) { window.addEventListener("keydown", h); document.body.style.overflow = "hidden"; }
    return () => { window.removeEventListener("keydown", h); document.body.style.overflow = ""; };
  }, [drawerOpen, closeCart]);

  if (!drawerOpen) return null;

  const goCheckout = () => { closeCart(); nav("/panier"); };

  return (
    <div className="cs-dr" role="dialog" aria-modal="true" aria-label="Panier" data-testid="cart-drawer">
      <style>{DR_CSS}</style>
      <div className="cs-dr-scrim" data-testid="cart-drawer-scrim" onClick={closeCart} />
      <aside className="cs-dr-panel">
        <div className="cs-dr-head">
          <h3>Ton panier {cartCount > 0 && <span className="cs-dr-badge">{cartCount}</span>}</h3>
          <button className="cs-dr-x" data-testid="cart-drawer-close" aria-label="Fermer le panier" onClick={closeCart}><X size={22} /></button>
        </div>

        {cart.length === 0 ? (
          <div className="cs-dr-empty" data-testid="cart-drawer-empty">
            <ShoppingBag size={40} className="text-[#C6531A]" />
            <p className="font-bold text-[#2B1A12] mt-3">Ton panier est vide</p>
            <p className="text-sm text-[#6E5546] mt-1">Découvre nos cookies et compose ta box.</p>
            <button className="cs-dr-cta mt-5" data-testid="cart-drawer-shop" onClick={() => { closeCart(); nav("/cookies"); }}>Voir la carte <ArrowRight size={17} /></button>
          </div>
        ) : (
          <>
            <ul className="cs-dr-lines">
              {cart.map((item) => (
                <CartLine key={item.line_id} item={item} prefix="drawer" updateQty={updateQty} removeFromCart={removeFromCart} />
              ))}
            </ul>

            <BoxIncentive cart={cart} />

            {!cart.every((i) => i.kind === "giftcard") && <ReceptionPicker subtotal={cartSubtotal} cart={cart} />}

            <div className="cs-dr-foot">
              <div className="cs-dr-totals">
                <div><span>Sous-total</span><span>{fmt(cartSubtotal)}</span></div>
                <div className="cs-dr-small"><span>Livraison</span><span>Confirmée à l'étape suivante</span></div>
              </div>
              <button className="cs-dr-cta" data-testid="cart-drawer-checkout" onClick={goCheckout}>Passer commande — {fmt(cartSubtotal)} <ArrowRight size={17} /></button>
              <p className="cs-dr-note">Retrait à Palaiseau • point relais dès 4,10 € • domicile dès 7,95 €</p>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

const DR_CSS = `
.cs-dr{--orange:#C6531A;--orange-deep:#A8410F;--orange-ink:#B44A16;--paper:#FFF8F1;--cocoa:#2B1A12;--muted:#6E5546;--line:#E7D3C2;font-family:'Bricolage Grotesque',ui-sans-serif,system-ui,sans-serif}
.cs-dr-scrim{position:fixed;inset:0;background:rgba(43,26,18,.5);z-index:70;animation:csdrfade .2s ease}
@keyframes csdrfade{from{opacity:0}to{opacity:1}}
.cs-dr-panel{position:fixed;top:0;right:0;bottom:0;width:min(480px,100%);background:var(--paper);z-index:71;display:flex;flex-direction:column;padding:20px;gap:16px;overflow-y:auto;box-shadow:-20px 0 40px rgba(43,26,18,.2);animation:csdrslide .28s cubic-bezier(.22,1,.36,1)}
@keyframes csdrslide{from{transform:translateX(100%)}to{transform:translateX(0)}}
.cs-dr-head{display:flex;justify-content:space-between;align-items:center}
.cs-dr-head h3{margin:0;font-size:26px;font-weight:800;letter-spacing:-0.03em;color:var(--cocoa);display:flex;align-items:center;gap:10px}
.cs-dr-badge{background:var(--orange-ink);color:#fff;font-size:14px;min-width:26px;height:26px;border-radius:999px;display:inline-grid;place-items:center;padding:0 6px}
.cs-dr-x{width:44px;height:44px;border-radius:999px;border:1px solid var(--line);background:#fff;color:var(--cocoa);display:grid;place-items:center;cursor:pointer}
.cs-dr-x:hover{background:#F3E6D3}
.cs-dr-lines{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:12px}
.cs-dr-foot{margin-top:auto;display:flex;flex-direction:column;gap:12px;padding-top:4px}
.cs-dr-totals{border-top:1px solid var(--line);padding-top:12px;display:flex;flex-direction:column;gap:4px;font-variant-numeric:tabular-nums}
.cs-dr-totals>div{display:flex;justify-content:space-between;font-size:15px;color:var(--cocoa)}
.cs-dr-totals .cs-dr-small{font-size:13px;color:var(--muted)}
.cs-dr-cta{min-height:54px;border-radius:999px;border:0;background:var(--orange-ink);color:#fff;font-weight:700;font-size:16px;display:inline-flex;align-items:center;justify-content:center;gap:8px;cursor:pointer;transition:background .2s}
.cs-dr-cta:hover{background:var(--orange-deep)}
.cs-dr-rec{display:flex;flex-direction:column;gap:10px}
.cs-dr-rec h4{margin:4px 0 2px;font-size:18px;font-weight:700;color:var(--cocoa);letter-spacing:-0.01em}
.cs-dr-rec button{display:flex;align-items:center;gap:14px;min-height:56px;padding:12px 18px;border-radius:16px;border:1.5px solid var(--line);background:#fff;color:var(--cocoa);font:inherit;font-size:15.5px;text-align:left;cursor:pointer;transition:border-color .15s,background-color .15s}
.cs-dr-rec button:hover{border-color:#D9A27F}
.cs-dr-rec button.on{border-color:var(--orange-ink)}
.cs-dr-radio{width:22px;height:22px;flex:none;border-radius:999px;border:1.5px solid #9C8B7E;display:grid;place-items:center}
.cs-dr-rec button.on .cs-dr-radio{border:0;background:var(--orange-ink)}
.cs-dr-rec button.on .cs-dr-radio::after{content:"";width:8px;height:8px;border-radius:999px;background:#fff}
.cs-dr-rec-l{flex:1;min-width:0}
.cs-dr-rec b{font-weight:700;white-space:nowrap;font-variant-numeric:tabular-nums}
.cs-dr-pk{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:4px}
.cs-dr-pk label{display:flex;flex-direction:column;gap:6px;font-size:14px;font-weight:700;color:var(--cocoa);min-width:0}
.cs-dr-pk select{font:inherit;font-weight:400;font-size:14.5px;min-height:48px;padding:0 12px;border-radius:14px;border:1.5px solid var(--line);background:#fff;color:var(--cocoa);width:100%;text-transform:capitalize}
.cs-dr-pk select:disabled{opacity:.55}
.cs-dr-fs{background:#fff;border:1.5px solid var(--line);border-radius:16px;padding:12px 16px}
.cs-dr-fs p{margin:0 0 8px;font-size:14px;font-weight:700;color:var(--orange-deep)}
.cs-dr-fs-bar{height:8px;border-radius:999px;background:#F3E6D3;overflow:hidden}
.cs-dr-fs-bar span{display:block;height:100%;background:var(--orange-ink);border-radius:999px;transition:width .3s ease}
.cs-dr-note{margin:0;font-size:12.5px;color:var(--muted);text-align:center}
.cs-dr-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:40px 10px}
`;
